import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Helmet } from "react-helmet";
import sliderConfig from "../data/slider.json";
import gcGallery from "../data/gc-gallery.json";
import packraftMeta from "../data/packraft-page-meta.json";
import PackraftGrandCanyonMap from "../components/PackraftGrandCanyonMap";
import "./GrandCanyonPackrafting.css";

const PACKRAFT_PAGE_TITLE = packraftMeta.title;
const PACKRAFT_PAGE_URL = packraftMeta.url;
const PACKRAFT_OG_IMAGE = packraftMeta.ogImage;
const PACKRAFT_PAGE_DESCRIPTION = packraftMeta.description;
const PACKRAFT_OG_IMAGE_ALT = packraftMeta.ogImageAlt;

const TOC_ITEMS = [
  { id: "permits", label: "Permits" },
  { id: "preparation", label: "Preparation" },
  { id: "gear-list", label: "Gear list" },
  { id: "travel", label: "Travel" },
  { id: "rapids-flow-chart", label: "Rapids & Flow" },
  { id: "floating-down", label: "FLOAT" },
  { id: "hiking-out", label: "Hike" },
  { id: "photos", label: "Photos" },
  { id: "trip-costs", label: "Costs" },
];

const LoveToHeart = () => {
  const [asHeart, setAsHeart] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setAsHeart(true), 12000);
    return () => window.clearTimeout(timer);
  }, []);

  if (asHeart) {
    return (
      <span className="packraft-heart" aria-label="love" role="img">
        ♥
      </span>
    );
  }

  return <span className="packraft-heart packraft-heart--word">love</span>;
};

const INTRO = [
  "This is a personal account—not park policy, not an outfitter brochure. I wrote it because I wanted an honest record of how I prepared for packrafting the Grand Canyon from Lee\u2019s Ferry to Pipe Creek: the spreadsheets, the fitness, the gear trials on local rivers, and the second-guessing that never fully goes away until you are below the rim.",
  "Our trip ran on a private, noncommercial river permit: your own boats, your own food, your own decisions. That kind of launch is harder to land than even The Wave permit in Vermilion Cliffs\u2014a draw we were lucky enough to check off last year, same three of us\u2014and we knew how steep the odds were. We did not win the big February main lottery; our launch came from a follow-up lottery (the one we actually won) instead. After the main draw, the park opens smaller lotteries whenever someone cancels or leaves a date unclaimed\u2014same permit, shorter notice, still competitive, but a real second chance for people who keep watching grcariverpermits.nps.gov. That is how the three of us ended up on the Colorado. I still feel lucky every time I think about it, and if you are dreaming of the same, you should understand the lottery before you buy a boat. The Permits section below is where I unpack how it works and what the odds actually look like.",
  "As much as we would have loved to run the entire canyon, work commitments got in the way\u2014and honestly, what would a packrafting trip be without a hike out? We debated hiking out via Deer Creek or Tapeats, but either option would have added at least three extra days to an already tight schedule, so we planned for Lee\u2019s Ferry to Pipe Creek and made peace with leaving the rest for another year.",
];

const isVideoSlide = (slide) =>
  slide.type === "video" || /\.(mp4|webm|mov)(\?|$)/i.test(slide.src || "");

const GALLERY_ROWS = 3;
const CURTEC_DRUM_PHOTO_SRC = "/assets/GC-gallery/IMG_1907.jpeg";

const getGalleryColumns = () => {
  if (window.matchMedia("(min-width: 768px)").matches) return 4;
  if (window.matchMedia("(min-width: 480px)").matches) return 3;
  return 2;
};

const chunkGalleryImages = (images, columns) => {
  const perPage = columns * GALLERY_ROWS;
  const pages = [];
  for (let i = 0; i < images.length; i += perPage) {
    pages.push(images.slice(i, i + perPage));
  }
  return pages;
};

const formatGearNotes = (gear, note) => {
  const g = gear && gear !== "—" ? gear : "";
  const n = note && note !== "—" ? note : "";
  if (g && n) return `${g} — ${n}`;
  return g || n || "—";
};

const gearWeightG = (grams) => `${grams}g`;

const sumGearWeightG = (weights) =>
  Object.values(weights).reduce((total, grams) => total + grams, 0);

const formatGearSectionTotal = (grams) => {
  const g = Math.round(grams);
  if (g >= 1000) {
    return `${(g / 1000).toFixed(2)} kg (${g.toLocaleString("en-US")} g)`;
  }
  return `${g} g`;
};

const GearSectionWeightTotal = ({ label, grams, className = "" }) => (
  <p className={`packraft-gear-total${className ? ` ${className}` : ""}`}>
    <span className="packraft-gear-total__label">{label}</span>
    {" — "}
    <span className="packraft-gear-total__value">{formatGearSectionTotal(grams)}</span>
    {" dry weight"}
  </p>
);

const PERSONAL_GEAR_WEIGHT_G = {
  raft: 4100,
  paddle: 1107,
  pfd: 770,
  helmet: 400,
  throwBag: 750,
  riverClothing: 500,
  gloves: 40,
  drysuit: 1400,
  water: 120,
  light: 95,
  backpack: 1115,
  tent: 1340,
  sleepingPad: 555,
  sleepingBag: 825,
  pillow: 82,
  chair: 637,
  iphone: 265,
  insta360: 310,
  anker: 742,
  campClothing: 960,
  cleaning: 80,
  tyvek: 245,
  shoes: 1220,
  hikingPole: 276,
  medical: 250,
  protection: 120,
  misc: 435,
};

const GROUP_GEAR_WEIGHT_G = {
  extraPaddle: 890,
  extraPfd: 620,
  gas: 700,
  signallingPad: 550,
  guideBookPrints: 70,
  guidebook: 450,
  inReach: 120,
  waterTreatment: 350,
  stove: 100,
  medicalSplint: 150,
  foodPork: 1200,
  foodApples: 1500,
  foodOil: 500,
  pot: 200,
};

const FOOD_PLAN_WEIGHT_G = {
  breakfast: 350,
  lunch: 1000,
  dinner: 1000,
  misc: 500,
};

const HUMAN_WASTE_WEIGHT_G = {
  drum: 415,
  wagBags: 480,
  gel: 100,
};

const TRIP_SHARED_EXPENSES_TOTAL_USD = 1464;

const GEAR_GROUP_TOTAL_G = sumGearWeightG(GROUP_GEAR_WEIGHT_G);
const GEAR_GROUP_PER_PERSON_G = GEAR_GROUP_TOTAL_G / 3;

const GEAR_GRAND_TOTAL_PER_PERSON_G =
  sumGearWeightG(PERSONAL_GEAR_WEIGHT_G) +
  GEAR_GROUP_PER_PERSON_G +
  sumGearWeightG(FOOD_PLAN_WEIGHT_G) +
  sumGearWeightG(HUMAN_WASTE_WEIGHT_G);

const GearGrandTotal = () => (
  <div className="packraft-gear-total packraft-gear-total--grand">
    <ul className="packraft-gear-total__breakdown">
      <li>
        <span className="packraft-gear-total__label">Personal gear</span>
        {" — "}
        <span className="packraft-gear-total__value">
          {formatGearSectionTotal(sumGearWeightG(PERSONAL_GEAR_WEIGHT_G))}
        </span>
      </li>
      <li>
        <span className="packraft-gear-total__label">Group gear</span>
        {" — "}
        <span className="packraft-gear-total__value">
          {formatGearSectionTotal(GEAR_GROUP_PER_PERSON_G)}
        </span>
        <span className="packraft-gear-total__note">
          {" "}
          (÷3; {formatGearSectionTotal(GEAR_GROUP_TOTAL_G)} shared)
        </span>
      </li>
      <li>
        <span className="packraft-gear-total__label">Food</span>
        {" — "}
        <span className="packraft-gear-total__value">
          {formatGearSectionTotal(sumGearWeightG(FOOD_PLAN_WEIGHT_G))}
        </span>
      </li>
      <li>
        <span className="packraft-gear-total__label">Human waste</span>
        {" — "}
        <span className="packraft-gear-total__value">
          {formatGearSectionTotal(sumGearWeightG(HUMAN_WASTE_WEIGHT_G))}
        </span>
      </li>
    </ul>
    <p className="packraft-gear-total__sum">
      <span className="packraft-gear-total__label">Grand total</span>
      {" — "}
      <span className="packraft-gear-total__value">
        {formatGearSectionTotal(GEAR_GRAND_TOTAL_PER_PERSON_G)}
      </span>
      {" dry weight (per person)"}
    </p>
  </div>
);

/* Lees Ferry (mile 0) through Pipe Creek (mile 89) — from gc.ursu.net tide chart */
const TIDE_CHART_LEES_TO_PIPE = [
  { location: "Lees Ferry", peak: "18:00", low: "6:00", mile: 0 },
  { location: "Badger", peak: "19:34", low: "7:58", mile: 8 },
  { location: "Soap Creek", peak: "20:09", low: "8:42", mile: 11 },
  { location: "Sheer Wall", peak: "20:45", low: "9:26", mile: 14 },
  { location: "House Rock", peak: "21:20", low: "10:10", mile: 17 },
  { location: "21 Mile", peak: "22:07", low: "11:09", mile: 21 },
  { location: "Indian Dick", peak: "22:31", low: "11:39", mile: 23 },
  { location: "25 Mile", peak: "22:55", low: "12:08", mile: 25 },
  { location: "29 Mile", peak: "23:42", low: "13:07", mile: 29 },
  { location: "South Canyon", peak: "0:05", low: "13:37", mile: 31 },
  { location: "Red Wall", peak: "0:29", low: "14:06", mile: 33 },
  { location: "36 Mile", peak: "1:04", low: "14:51", mile: 36 },
  { location: "Buck Farm", peak: "2:03", low: "16:04", mile: 42 },
  { location: "Pres. Harding", peak: "2:39", low: "16:49", mile: 44 },
  { location: "Saddle Canyon", peak: "3:14", low: "17:33", mile: 47 },
  { location: "Nankoweap", peak: "4:13", low: "18:47", mile: 52 },
  { location: "Kwagunt", peak: "5:00", low: "19:46", mile: 56 },
  { location: "Little Colorado", peak: "6:00", low: "21:00", mile: 61 },
  { location: "Lava Chuar", peak: "6:55", low: "21:27", mile: 65 },
  { location: "Tanner Canyon", peak: "7:36", low: "21:48", mile: 68 },
  { location: "Unkar Rapid", peak: "8:46", low: "22:23", mile: 73 },
  { location: "Nevills Rapid", peak: "9:13", low: "22:36", mile: 75 },
  { location: "Hance Rapid", peak: "9:41", low: "22:50", mile: 77 },
  { location: "Sockdolager", peak: "10:09", low: "23:04", mile: 79 },
  { location: "Grapevine", peak: "10:50", low: "23:25", mile: 82 },
  { location: "Phantom Ranch", peak: "12:00", low: "0:00", mile: 87 },
  { location: "Pipe Creek", peak: "12:20", low: "0:20", mile: 89 },
];

function GrandCanyonPackrafting() {
  const { autoAdvance = false, delayMs = 5000, slides } = sliderConfig;
  const videoRefs = useRef([]);
  const [activeSlide, setActiveSlide] = useState(0);
  const [videoMuted, setVideoMuted] = useState(true);
  const [videoPaused, setVideoPaused] = useState(false);
  const [parallaxOffset, setParallaxOffset] = useState(0);
  const heroStackRef = useRef(null);
  const titlesRef = useRef(null);
  const stickyChromeRef = useRef(null);
  const [titlesPinned, setTitlesPinned] = useState(false);
  const [stickyChromeHeight, setStickyChromeHeight] = useState(0);
  const tocSentinelRef = useRef(null);
  const tocRef = useRef(null);
  const [tocStuck, setTocStuck] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [galleryColumns, setGalleryColumns] = useState(3);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const galleryTouchStartX = useRef(null);

  const isDesktopGallery = () =>
    window.matchMedia("(min-width: 768px)").matches;

  const openLightboxForSrc = (src) => {
    const index = gcGallery.images.findIndex((image) => image.src === src);
    if (index >= 0) setLightboxIndex(index);
  };

  const openGalleryLightbox = (src) => {
    if (!isDesktopGallery()) return;
    openLightboxForSrc(src);
  };

  const closeGalleryLightbox = useCallback(() => {
    setLightboxIndex(null);
  }, []);

  const stepGalleryLightbox = useCallback((delta) => {
    setLightboxIndex(
      (index) =>
        (index + delta + gcGallery.images.length) % gcGallery.images.length
    );
  }, []);

  useEffect(() => {
    if (lightboxIndex == null) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") closeGalleryLightbox();
      if (event.key === "ArrowLeft") stepGalleryLightbox(-1);
      if (event.key === "ArrowRight") stepGalleryLightbox(1);
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [lightboxIndex, closeGalleryLightbox, stepGalleryLightbox]);

  const galleryPages = useMemo(
    () => chunkGalleryImages(gcGallery.images, galleryColumns),
    [galleryColumns]
  );

  useEffect(() => {
    const updateColumns = () => setGalleryColumns(getGalleryColumns());
    updateColumns();
    window.addEventListener("resize", updateColumns);
    return () => window.removeEventListener("resize", updateColumns);
  }, []);

  useEffect(() => {
    setGalleryIndex((index) =>
      galleryPages.length ? Math.min(index, galleryPages.length - 1) : 0
    );
  }, [galleryPages.length]);

  const goToGalleryPage = (index) => {
    if (!galleryPages.length) return;
    setGalleryIndex((index + galleryPages.length) % galleryPages.length);
  };

  const handleGalleryTouchStart = (event) => {
    galleryTouchStartX.current = event.touches[0].clientX;
  };

  const handleGalleryTouchEnd = (event) => {
    if (galleryTouchStartX.current == null) return;
    const delta = event.changedTouches[0].clientX - galleryTouchStartX.current;
    if (delta < -48) goToGalleryPage(galleryIndex + 1);
    else if (delta > 48) goToGalleryPage(galleryIndex - 1);
    galleryTouchStartX.current = null;
  };

  const measureStickyChrome = useCallback(() => {
    if (stickyChromeRef.current && titlesPinned) {
      setStickyChromeHeight(stickyChromeRef.current.offsetHeight);
    }
  }, [titlesPinned]);

  useEffect(() => {
    document.title = PACKRAFT_PAGE_TITLE;
  }, []);

  useEffect(() => {
    const scrollToHash = () => {
      const id = window.location.hash.replace(/^#/, "");
      if (!id) return false;
      const el = document.getElementById(id);
      if (!el) return false;
      el.scrollIntoView({ behavior: "auto", block: "start" });
      return true;
    };

    if (scrollToHash()) return undefined;

    // SPA mount: hash target may not exist until after first paint / layout
    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;
      if (scrollToHash() || attempts >= 20) {
        window.clearInterval(timer);
      }
    }, 50);

    const onHashChange = () => {
      window.requestAnimationFrame(scrollToHash);
    };
    window.addEventListener("hashchange", onHashChange);

    return () => {
      window.clearInterval(timer);
      window.removeEventListener("hashchange", onHashChange);
    };
  }, []);

  useEffect(() => {
    if (!autoAdvance || slides.length <= 1) return undefined;
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slides.length);
    }, delayMs);
    return () => clearInterval(timer);
  }, [autoAdvance, delayMs, slides.length]);

  useEffect(() => {
    setVideoPaused(false);
  }, [activeSlide]);

  useEffect(() => {
    videoRefs.current.forEach((video, index) => {
      if (!video) return;
      if (index === activeSlide) {
        video.muted = videoMuted;
        if (videoPaused) {
          video.pause();
        } else {
          video.play().catch(() => {});
        }
      } else {
        video.pause();
        video.currentTime = 0;
      }
    });
  }, [activeSlide, slides, videoMuted, videoPaused]);

  const activeIsVideo = isVideoSlide(slides[activeSlide]);

  const goToSlide = (index) => {
    setActiveSlide((index + slides.length) % slides.length);
  };

  const goToPrevSlide = () => goToSlide(activeSlide - 1);
  const goToNextSlide = () => goToSlide(activeSlide + 1);

  const handleScroll = useCallback(() => {
    const isDesktop = window.matchMedia("(min-width: 768px)").matches;
    const heroHeight =
      heroStackRef.current?.offsetHeight ??
      (isDesktop
        ? Math.max(window.innerHeight * 0.8, 560)
        : Math.max(window.innerHeight * 0.4, 280));
    const y = Math.min(window.scrollY, heroHeight);
    setParallaxOffset(y * 0.45);
    setTitlesPinned(window.scrollY >= heroHeight);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    if (!titlesPinned) {
      setStickyChromeHeight(0);
      return undefined;
    }

    const el = stickyChromeRef.current;
    if (!el) return undefined;

    const update = () => measureStickyChrome();
    update();
    requestAnimationFrame(update);

    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [titlesPinned, tocStuck, measureStickyChrome]);

  useEffect(() => {
    const sentinel = tocSentinelRef.current;
    if (!sentinel) return undefined;

    const topInset = titlesPinned ? stickyChromeHeight : 0;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setTocStuck(!entry.isIntersecting && titlesPinned);
      },
      {
        threshold: 0,
        rootMargin: `-${topInset}px 0px 0px 0px`,
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [titlesPinned, stickyChromeHeight]);

  const updateActiveSection = useCallback(() => {
    const offset =
      (titlesPinned ? stickyChromeHeight : 0) + (tocStuck ? 8 : 64);
    let current = null;

    for (const { id } of TOC_ITEMS) {
      const el = document.getElementById(id);
      if (!el) continue;
      if (el.getBoundingClientRect().top <= offset) {
        current = id;
      }
    }

    setActiveSection(current);
  }, [titlesPinned, tocStuck, stickyChromeHeight]);

  useEffect(() => {
    window.addEventListener("scroll", updateActiveSection, { passive: true });
    updateActiveSection();
    return () => window.removeEventListener("scroll", updateActiveSection);
  }, [updateActiveSection]);

  const renderTocLinks = () => (
    <ol className="packraft-toc__list">
      {TOC_ITEMS.map((item) => (
        <li key={item.id}>
          <a
            href={`#${item.id}`}
            className={
              activeSection === item.id ? "packraft-toc__link--active" : undefined
            }
            aria-current={activeSection === item.id ? "location" : undefined}
          >
            {item.label}
          </a>
        </li>
      ))}
    </ol>
  );

  return (
    <article className="packraft-article">
      <Helmet>
        <title>{PACKRAFT_PAGE_TITLE}</title>
        <meta name="description" content={PACKRAFT_PAGE_DESCRIPTION} />
        <link rel="canonical" href={PACKRAFT_PAGE_URL} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Victor Ursu" />
        <meta property="og:title" content={PACKRAFT_PAGE_TITLE} />
        <meta property="og:description" content={PACKRAFT_PAGE_DESCRIPTION} />
        <meta property="og:url" content={PACKRAFT_PAGE_URL} />
        <meta property="og:image" content={PACKRAFT_OG_IMAGE} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={PACKRAFT_OG_IMAGE_ALT} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={PACKRAFT_PAGE_TITLE} />
        <meta name="twitter:description" content={PACKRAFT_PAGE_DESCRIPTION} />
        <meta name="twitter:image" content={PACKRAFT_OG_IMAGE} />
        <meta name="twitter:image:alt" content={PACKRAFT_OG_IMAGE_ALT} />
      </Helmet>

      <div className="packraft-hero-stack" ref={heroStackRef}>
      <header className="packraft-hero" aria-label="Article hero">
        <div
          className="packraft-hero__slides"
          style={{ transform: `translate3d(0, ${parallaxOffset}px, 0)` }}
          aria-hidden="true"
        >
          {slides.map((slide, index) => {
            const isActive = index === activeSlide;
            const isVideo = isVideoSlide(slide);

            return (
              <div
                key={`${slide.src}-${index}`}
                className={`packraft-hero__slide${
                  isActive ? " packraft-hero__slide--active" : ""
                }${isVideo ? " packraft-hero__slide--video" : ""}`}
                style={
                  isVideo ? undefined : { backgroundImage: `url(${slide.src})` }
                }
                role={isVideo ? undefined : "img"}
                aria-label={isVideo ? undefined : slide.alt}
              >
                {isVideo && (
                  <video
                    ref={(el) => {
                      videoRefs.current[index] = el;
                    }}
                    className="packraft-hero__slide-video"
                    src={slide.src}
                    muted
                    loop
                    playsInline
                    aria-label={slide.alt}
                    preload="metadata"
                  />
                )}
              </div>
            );
          })}
        </div>
        <div
          className={`packraft-hero__overlay${
            activeIsVideo ? " packraft-hero__overlay--video" : ""
          }`}
        />
        {activeIsVideo && (
          <div className="packraft-hero__video-controls">
            <button
              type="button"
              className="packraft-hero__video-control"
              aria-label={videoPaused ? "Play video" : "Pause video"}
              onClick={() => setVideoPaused((paused) => !paused)}
            >
              {videoPaused ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="22"
                  height="22"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <polygon points="8 5 19 12 8 19 8 5" />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="22"
                  height="22"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <rect x="6" y="5" width="4" height="14" rx="1" />
                  <rect x="14" y="5" width="4" height="14" rx="1" />
                </svg>
              )}
            </button>
            <button
              type="button"
              className="packraft-hero__video-control"
              aria-label={videoMuted ? "Unmute video" : "Mute video"}
              onClick={() => setVideoMuted((muted) => !muted)}
            >
              {videoMuted ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="22"
                  height="22"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                  <line x1="23" y1="9" x2="17" y2="15" />
                  <line x1="17" y1="9" x2="23" y2="15" />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="22"
                  height="22"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                  <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                  <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                </svg>
              )}
            </button>
          </div>
        )}
        {slides.length > 1 && (
          <>
            <button
              type="button"
              className="packraft-hero__nav packraft-hero__nav--prev"
              aria-label="Previous slide"
              onClick={goToPrevSlide}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="28"
                height="28"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <button
              type="button"
              className="packraft-hero__nav packraft-hero__nav--next"
              aria-label="Next slide"
              onClick={goToNextSlide}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="28"
                height="28"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
            <div className="packraft-hero__dots" role="tablist" aria-label="Hero slides">
              {slides.map((slide, index) => (
                <button
                  key={`${slide.src}-${index}`}
                  type="button"
                  role="tab"
                  aria-selected={index === activeSlide}
                  aria-label={`Slide ${index + 1}: ${slide.alt}`}
                  className={`packraft-hero__dot${
                    index === activeSlide ? " packraft-hero__dot--active" : ""
                  }`}
                  onClick={() => goToSlide(index)}
                />
              ))}
            </div>
          </>
        )}
      </header>
        {!titlesPinned && (
          <div ref={titlesRef} className="packraft-hero__titles">
            <h1 className="packraft-hero__title">
              How to packraft the Grand Canyon
            </h1>
            <p className="packraft-hero__subtitle">
              from Lee&apos;s Ferry to Pipe Creek
            </p>
          </div>
        )}
      </div>

      {titlesPinned && (
        <>
          <div ref={stickyChromeRef} className="packraft-sticky-chrome">
            <div
              ref={titlesRef}
              className="packraft-hero__titles packraft-hero__titles--pinned"
            >
              <h1 className="packraft-hero__title">
                How to packraft the Grand Canyon
              </h1>
              <p className="packraft-hero__subtitle">
                from Lee&apos;s Ferry to Pipe Creek
              </p>
            </div>
            {tocStuck && (
              <nav
                ref={tocRef}
                className="packraft-toc packraft-toc--stuck"
                aria-label="On this page"
              >
                <h2
                  id="packraft-toc-heading"
                  className="packraft-toc__heading packraft-toc__heading--hidden"
                >
                  On this page
                </h2>
                {renderTocLinks()}
              </nav>
            )}
          </div>
          <div
            className="packraft-sticky-chrome-spacer"
            style={{ height: stickyChromeHeight }}
            aria-hidden="true"
          />
        </>
      )}

      <div
        className="packraft-body"
        style={{
          "--sticky-chrome-height": `${stickyChromeHeight}px`,
        }}
      >
        <div className="packraft-intro">
          {INTRO.map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </div>

        <PackraftGrandCanyonMap />

        <div ref={tocSentinelRef} className="packraft-toc-sentinel" aria-hidden="true" />

        <div className="packraft-intro packraft-intro--before-permits">
          <p>
            This wasn&apos;t just a Grand Canyon river trip—it was a true packrafting
            journey.
          </p>
          <p>
            We traveled by both river and trail, carrying everything we needed with
            us. The raft wasn&apos;t simply a way to move downstream; it was one part
            of a self-supported adventure that blended paddling and hiking into a
            single experience.
          </p>
          <p>
            To finish the journey, we shouldered our rafts and backpacks and climbed
            nearly 5,000 vertical feet out of the canyon under our own power. That
            final ascent was as much a part of the adventure as the river itself.
          </p>
        </div>

        <section id="permits" className="packraft-section">
          <h2 className="packraft-section__title">Permits</h2>
          <p>
            Every private trip on the Colorado through Grand Canyon National Park
            needs a <strong>noncommercial river permit</strong>. Commercial trips are
            a separate world; what we ran was the self-guided model—one permit holder,
            a listed group, up to sixteen people on a standard trip, launch at
            Lee&apos;s Ferry, and strict rules on boats, waste, and take-out. Without
            that paper, you are not legally on the river, no matter how light your
            packraft is.
          </p>
          <p>
            Since 2006 the park has used a <strong>weighted lottery</strong> instead
            of a decades-long waitlist. Each February, applicants log into{" "}
            <a
              href="https://grcariverpermits.nps.gov"
              target="_blank"
              rel="noopener noreferrer"
            >
              grcariverpermits.nps.gov
            </a>{" "}
            and bid on launch dates for the following year. You pay a $25 application
            fee once per launch year. If you win, you immediately owe a trip deposit
            and then full permit fees per person—none of it refundable if you change
            your mind late.
          </p>
          <p>
            Your lottery weight is measured in <strong>points from 1 to 5</strong>.
            First-time applicants and people who have not boated the canyon (or entered
            the lottery) in five or more years start at five points—the best odds the
            system allows. Points drop each year you apply without winning, which pushes
            repeat players toward longer timelines. NPS publishes statistics after
            each lottery; the pattern is brutal for popular windows.
          </p>
          <ul className="packraft-list">
            <li>
              <strong>Peak season (roughly May–September):</strong> many launch
              dates see hundreds or thousands of applications for a single slot.
              NPS data for recent years show success rates often in the{" "}
              <strong>1–5% range</strong> for desirable spring and fall dates, and
              worse for the most requested summer and early-September launches.
            </li>
            <li>
              <strong>Shoulder and winter:</strong> odds improve on colder, shorter
              days—some winter dates see far fewer applicants, and follow-up lotteries
              occasionally offer dates that went unclaimed in the main draw.
            </li>
            <li>
              <strong>Scale:</strong> on the order of{" "}
              <strong>450 noncommercial launches per year</strong> from Lee&apos;s
              Ferry, while the main lottery alone can draw{" "}
              <strong>8,000+ applications</strong> in a single year. Most people who
              eventually boat apply multiple years in a row; three to five years is a
              common refrain, and some chase a permit for much longer.
            </li>
          </ul>
          <aside className="packraft-note">
            <p>
              <strong>Note:</strong> Peak season might be considered roughly
              May–September for rafting. For packrafting, the windows that make more
              sense are often end of April through mid-May, or late September through
              the beginning of October. Given the high summer temperatures, I highly
              doubt anyone other than Kenton Grua or Kevin Fedarko would enjoy hiking
              out 5,000 feet to the rim on a hot summer day.
            </p>
          </aside>
          <p>
            Follow-up lotteries run through the year for cancelled or unclaimed dates.
            That is how some groups—including friends of friends—end up on the river
            on shorter notice. We treated our launch as the long shot it was: grateful,
            not entitled. If you are planning your own trip, apply every February,
            list flexible dates, read the{" "}
            <a
              href="https://www.nps.gov/grca/planyourvisit/weightedlottery.htm"
              target="_blank"
              rel="noopener noreferrer"
            >
              official weighted-lottery page
            </a>
            , and study the annual statistics PDFs the park posts. Hope for five
            points; plan as if you will need patience.
          </p>
        </section>

        <section id="preparation" className="packraft-section">
          <h2 className="packraft-section__title">Preparation</h2>
          <p>
            We won our follow-up lottery permit on <strong>March 19</strong>, and
            that is when prep actually started—we had drawn a launch on{" "}
            <strong>May 16</strong>, barely a month away. Suddenly the trip was real: calls to
            find team members who could commit on short notice, a shared list of gear
            we still had to buy, and a longer list of what we already owned but had
            to pack, carry, and trust to keep us alive for two weeks on the Colorado.
          </p>
          <p>
            The permit was a standard launch—up to sixteen people—but only three of us
            were crazy enough to commit: Breki, Jon, and myself. After the first week
            we even stopped calling friends; by then it was obvious nobody else would
            have enough prep time.
          </p>
          <p>
            The East Coast is not the best place to train for something like this. We
            had run water in Romania, France, and even the Escalante—but none of it
            qualified as &ldquo;rapids&rdquo; in the Grand Canyon sense, or so we told
            ourselves. Still, we managed to fit in river time on the Potomac and on local
            lakes and creeks—mostly flips and self-rescue until those moves felt boring,
            but also packing and unpacking the raft, loading dry bags inside the tubes,
            and learning how much friction a fully stuffed boat actually has.
          </p>
          <p>
            After a while we realized the hardest challenge might not be the river at
            all—it would be the hike out with heavily loaded packs, overnight from Pipe
            Creek to the top of the South Rim. We did not train for that specifically.
            I did run a mile a day, or at least twenty minutes, mostly so my legs would
            not be a complete surprise. The three of us are cavers, used to long hauls
            with heavy loads (in the dark, ha).
          </p>
          <p>
            From there it was spreadsheets, group texts, and dry runs on local water.
            Build fitness for long flatwater days, practice self-rescue and packraft
            inflation, and scout portage options around major rapids before you launch.
            We had roughly a month to turn a lucky lottery win into a plan none of us
            could wing.
          </p>
        </section>

        <section id="gear-list" className="packraft-section">
          <h2 className="packraft-section__title">Gear list</h2>
          <p>
            This is my personal kit. Breki and Jon&apos;s setups varied slightly—the
            main difference being they ran <strong>MRS Alligator 2S Pro</strong> rafts
            while I was on the Alpacka.
          </p>

          <h3 className="packraft-gear-table__title">Personal gear</h3>
          <p className="packraft-gear-legend">
            <span className="packraft-gear-legend__swatch" aria-hidden="true" />
            Blue rows — river gear
          </p>
          <GearSectionWeightTotal
            label="Personal gear"
            grams={sumGearWeightG(PERSONAL_GEAR_WEIGHT_G)}
          />
          <div className="packraft-table-wrap">
            <table className="packraft-gear-table">
              <thead>
                <tr>
                  <th scope="col">Item</th>
                  <th scope="col">Notes</th>
                  <th scope="col">Weight</th>
                </tr>
              </thead>
              <tbody>
                <tr className="packraft-table-row--river">
                  <td>Raft</td>
                  <td>
                    <a
                      href="https://alpackaraft.com/products/expedition"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Alpacka Expedition L - RTS WW Deck
                    </a>
                    <span className="packraft-gear-table__detail">
                      Bow bag + inflator bag + two waterproof bag inserts + all the rope and webbing
                      that was attached
                    </span>
                  </td>
                  <td>{gearWeightG(PERSONAL_GEAR_WEIGHT_G.raft)}</td>
                </tr>
                <tr className="packraft-table-row--river">
                  <td>Paddle</td>
                  <td>
                    <a
                      href="https://alpackaraft.com/products/werner-sherpa?blade-color-length=amber-200cm"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Werner Sherpa
                    </a>
                    {" — The cool yellow—looks good in photos"}
                  </td>
                  <td>{gearWeightG(PERSONAL_GEAR_WEIGHT_G.paddle)}</td>
                </tr>
                <tr className="packraft-table-row--river">
                  <td>PFD</td>
                  <td>
                    <a
                      href="https://www.nrs.com/nrs-ninja-pfd/p7fb"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      NRS Ninja
                    </a>
                    {" + "}
                    <a
                      href="https://www.amazon.com/dp/B0CYDG62XF"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      NRS knife
                    </a>
                    {" + little emergency mirror"}
                  </td>
                  <td>{gearWeightG(PERSONAL_GEAR_WEIGHT_G.pfd)}</td>
                </tr>
                <tr className="packraft-table-row--river">
                  <td>Helmet</td>
                  <td>
                    <a
                      href="https://www.amazon.com/dp/B0G2T4S6ZB"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      NRS Neso
                    </a>
                  </td>
                  <td>{gearWeightG(PERSONAL_GEAR_WEIGHT_G.helmet)}</td>
                </tr>
                <tr className="packraft-table-row--river">
                  <td>Throw bag</td>
                  <td>
                    <a
                      href="https://salamanderpaddlegear.com/product/salamander-retriever-and-golden-retriever-throw-bag/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Salamander Golden Retriever Throw Bag
                    </a>
                    {" — Felt like overkill"}
                  </td>
                  <td>{gearWeightG(PERSONAL_GEAR_WEIGHT_G.throwBag)}</td>
                </tr>
                <tr className="packraft-table-row--river">
                  <td>Clothing</td>
                  <td>
                    {formatGearNotes(
                      "River clothes — socks, thermal long johns",
                      "Washed and rinsed every night; dry by morning"
                    )}
                  </td>
                  <td>{gearWeightG(PERSONAL_GEAR_WEIGHT_G.riverClothing)}</td>
                </tr>
                <tr className="packraft-table-row--river">
                  <td>Gloves</td>
                  <td>
                    <a
                      href="https://www.amazon.com/dp/B088ZYNDSK?th=1&psc=1"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Kayaker Gloves
                    </a>
                    {" — Protect against the sun while paddling"}
                  </td>
                  <td>{gearWeightG(PERSONAL_GEAR_WEIGHT_G.gloves)}</td>
                </tr>
                <tr className="packraft-table-row--river">
                  <td>Drysuit</td>
                  <td>
                    <a
                      href="https://www.aliexpress.us/item/3256805365514100.html"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      AliExpress
                    </a>
                  </td>
                  <td>{gearWeightG(PERSONAL_GEAR_WEIGHT_G.drysuit)}</td>
                </tr>
                <tr>
                  <td>Water</td>
                  <td>
                    {formatGearNotes(
                      "2× 1 L Smartwater + 2× 1 L Platypus-style containers",
                      "—"
                    )}
                  </td>
                  <td>{gearWeightG(PERSONAL_GEAR_WEIGHT_G.water)}</td>
                </tr>
                <tr>
                  <td>Light</td>
                  <td>
                    2× Nitecore NU25
                    <span className="packraft-gear-table__detail">
                      Rechargeable USB-C — came in handy when Breki&apos;s headlamp died
                    </span>
                  </td>
                  <td>{gearWeightG(PERSONAL_GEAR_WEIGHT_G.light)}</td>
                </tr>
                <tr>
                  <td>Backpack</td>
                  <td>
                    <a
                      href="https://hyperlitemountaingear.com/products/southwest"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Hyperlite Mountain Gear Southwest 70L
                    </a>
                    {" — Internal Aluminum frame"}
                  </td>
                  <td>{gearWeightG(PERSONAL_GEAR_WEIGHT_G.backpack)}</td>
                </tr>
                <tr>
                  <td>Tent</td>
                  <td>
                    <a
                      href="https://www.bigagnes.com/products/tiger-wall-ul-one"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Big Agnes Tiger Wall UL 1 
                    </a> and tent footprint
                    {" — Solution-dyed"}
                  </td>
                  <td>{gearWeightG(PERSONAL_GEAR_WEIGHT_G.tent)}</td>
                </tr>
                <tr>
                  <td>Sleeping pad</td>
                  <td>
                    <a
                      href="https://www.rei.com/product/241034/therm-a-rest-neoair-xlite-nxt-sleeping-pad?sku=2410340004"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Thermarest NeoAir XLite NXT (Yellow)
                    </a>
                  </td>
                  <td>{gearWeightG(PERSONAL_GEAR_WEIGHT_G.sleepingPad)}</td>
                </tr>
                <tr>
                  <td>Sleeping bag</td>
                  <td>
                    <a
                      href="https://enlightenedequipment.com/enigma-sleeping-quilt/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Enlightened Equipment Enigma 20F quilt
                    </a>
                  </td>
                  <td>{gearWeightG(PERSONAL_GEAR_WEIGHT_G.sleepingBag)}</td>
                </tr>
                <tr>
                  <td>Pillow</td>
                  <td>{formatGearNotes("Nemo Fillo", "—")}</td>
                  <td>{gearWeightG(PERSONAL_GEAR_WEIGHT_G.pillow)}</td>
                </tr>
                <tr>
                  <td>Chair</td>
                  <td>
                    <a
                      href="https://www.rei.com/product/228134/nemo-moonlite-elite-reclining-backpacking-chair"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Nemo Moonlite Elitte
                    </a>
                  </td>
                  <td>{gearWeightG(PERSONAL_GEAR_WEIGHT_G.chair)}</td>
                </tr>
                <tr>
                  <td>Electronics</td>
                  <td>{formatGearNotes("iPhone 17 Pro Max", "—")}</td>
                  <td>{gearWeightG(PERSONAL_GEAR_WEIGHT_G.iphone)}</td>
                </tr>
                <tr>
                  <td>Electronics</td>
                  <td>{formatGearNotes("Insta360 X5", "—")}</td>
                  <td>{gearWeightG(PERSONAL_GEAR_WEIGHT_G.insta360)}</td>
                </tr>
                <tr>
                  <td>Electronics</td>
                  <td>{formatGearNotes("Anker 737", "—")}</td>
                  <td>{gearWeightG(PERSONAL_GEAR_WEIGHT_G.anker)}</td>
                </tr>
                <tr>
                  <td>Clothing</td>
                  <td>
                    {formatGearNotes(
                      "Camp clothes / dry clothes",
                      "1× shorts, 1× underwear, 1× t-shirt, 1 pair dry socks, puffer jacket, warm hat, 1 baseball hat for sun protection + extra top"
                    )}
                  </td>
                  <td>{gearWeightG(PERSONAL_GEAR_WEIGHT_G.campClothing)}</td>
                </tr>
                <tr>
                  <td>Cleaning</td>
                  <td>
                    Sponge and a Brush
                    <span className="packraft-gear-table__detail">
                      We did not run self-bailers (we had a skirt); however, water
                      would still make its way inside the cockpit—sometimes it was much
                      easier to use the sponge.
                      <br />
                      <br />
                      <strong>Brush</strong> — swipe sand off our Tyvek ground cloth and
                      wipe the T-Zip or zippers before opening.
                    </span>
                  </td>
                  <td>{gearWeightG(PERSONAL_GEAR_WEIGHT_G.cleaning)}</td>
                </tr>
                <tr>
                  <td>Tyvek</td>
                  <td>
                    9×5 ft ground cloth
                    <span className="packraft-gear-table__detail">
                      A Tyvek sheet used as a ground cloth to load and unload gear inside
                      the packraft—mainly to keep sand out and avoid damaging the zipper.
                      We also used lubricant or lubricant wipes every time we closed the
                      T-Zip.
                    </span>
                  </td>
                  <td>{gearWeightG(PERSONAL_GEAR_WEIGHT_G.tyvek)}</td>
                </tr>
                <tr>
                  <td>Shoes</td>
                  <td>
                    {formatGearNotes(
                      "2× Altra Lone Peak",
                      "One pair for the water; one for day hikes and camp"
                    )}
                  </td>
                  <td>{gearWeightG(PERSONAL_GEAR_WEIGHT_G.shoes)}</td>
                </tr>
                <tr>
                  <td>Hiking pole</td>
                  <td>
                    <a
                      href="https://us.komperdell.com/Camera-Staff-PL/K1762300-10-Tele"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Komperdell Camera-Staff-PL
                    </a>
                  </td>
                  <td>{gearWeightG(PERSONAL_GEAR_WEIGHT_G.hikingPole)}</td>
                </tr>
                <tr>
                  <td>Medical</td>
                  <td>
                    <a
                      href="https://www.amazon.com/dp/B0DV6PDY9R"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Personal first aid kit
                    </a>
                    <span className="packraft-gear-table__detail">
                      I&apos;ve added Leukotape (1 roll), a small container with pills
                      (ibuprofen, Tylenol, Motrin), nail clipper
                    </span>
                  </td>
                  <td>{gearWeightG(PERSONAL_GEAR_WEIGHT_G.medical)}</td>
                </tr>
                <tr>
                  <td>Protection</td>
                  <td>
                    {formatGearNotes(
                      "Sunscreen + moisturizing hand and face cream + beeswax + tooth paste, brush, soap ",
                      "—"
                    )}
                  </td>
                  <td>{gearWeightG(PERSONAL_GEAR_WEIGHT_G.protection)}</td>
                </tr>
                <tr>
                  <td>Misc</td>
                  <td>
                    Miscellaneous items that I was able to pack in pockets or
                    Hilltop Packs dyneema pouches
                    <span className="packraft-gear-table__detail">
                      AirPods, small tripod, Insta360 invisible stick, a couple of
                      memory cards, DJI microphone, pocket knife, small locking
                      carabiners × 4, charging cables, space blanket, wallet with 40$ cash, spork
                    </span>
                  </td>
                  <td>{gearWeightG(PERSONAL_GEAR_WEIGHT_G.misc)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="packraft-gear-table__title">Group gear (shared)</h3>
          <GearSectionWeightTotal
            label="Group gear"
            grams={sumGearWeightG(GROUP_GEAR_WEIGHT_G)}
          />
          <div className="packraft-table-wrap">
            <table className="packraft-gear-table">
              <thead>
                <tr>
                  <th scope="col">Item</th>
                  <th scope="col">Notes</th>
                  <th scope="col">Weight</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Extra paddle</td>
                  <td>
                    <a
                      href="https://www.rei.com/product/C04632/kokopelli-packraft-poudre-adventure-4-piece-paddle"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Kokopelli Packraft Poudre Adventure 4-Piece Paddle
                    </a>
                  </td>
                  <td>{gearWeightG(GROUP_GEAR_WEIGHT_G.extraPaddle)}</td>
                </tr>
                <tr>
                  <td>Extra PFD</td>
                  <td>{formatGearNotes("Astral EV-Eight", "—")}</td>
                  <td>{gearWeightG(GROUP_GEAR_WEIGHT_G.extraPfd)}</td>
                </tr>
                <tr>
                  <td>Gas</td>
                  <td>{formatGearNotes("Two medium size", "—")}</td>
                  <td>{gearWeightG(GROUP_GEAR_WEIGHT_G.gas)}</td>
                </tr>
                <tr>
                  <td>Signalling pad</td>
                  <td>
                    <a
                      href="https://www.riversports.com/products/emergency-rescue-signal-panels"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      4Corners Riversports
                    </a>
                  </td>
                  <td>{gearWeightG(GROUP_GEAR_WEIGHT_G.signallingPad)}</td>
                </tr>
                <tr>
                  <td>Guide book</td>
                  <td>
                    <a
                      href="https://www.nrs.com/colorado-river-in-grand-canyon-8th-ed/pcbi"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Tom Martin&apos;s river guidebook
                    </a>
                    <span className="packraft-gear-table__detail">
                      Printouts of our section on A5 TerraSlate paper that could be
                      stored in a PFD
                    </span>
                  </td>
                  <td>{gearWeightG(GROUP_GEAR_WEIGHT_G.guideBookPrints)}</td>
                </tr>
                <tr>
                  <td>Guidebook</td>
                  <td>
                    <a
                      href="https://www.nrs.com/colorado-river-in-grand-canyon-8th-ed/pcbi"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Tom Martin&apos;s river guidebook
                    </a>
                  </td>
                  <td>{gearWeightG(GROUP_GEAR_WEIGHT_G.guidebook)}</td>
                </tr>
                <tr>
                  <td>inReach</td>
                  <td>inReach Emergency Phone - borrowed</td>
                  <td>{gearWeightG(GROUP_GEAR_WEIGHT_G.inReach)}</td>
                </tr>
                <tr>
                  <td>Water treatment</td>
                  <td>{formatGearNotes("2 x Sawyer Squeeze 2liters+ Microdyne", "—")}</td>
                  <td>{gearWeightG(GROUP_GEAR_WEIGHT_G.waterTreatment)}</td>
                </tr>
                <tr>
                  <td>Stove</td>
                  <td>
                    Two stoves - BRS3000 and MSR Pocketrocket
                    <span className="packraft-gear-table__detail">
                      We picked this over the much better Jetboil because of weight
                    </span>
                  </td>
                  <td>{gearWeightG(GROUP_GEAR_WEIGHT_G.stove)}</td>
                </tr>
                <tr>
                  <td>Medical</td>
                  <td>Splint - Rhino Rescue</td>
                  <td>{gearWeightG(GROUP_GEAR_WEIGHT_G.medicalSplint)}</td>
                </tr>
                <tr>
                  <td>Food</td>
                  <td>
                    1 kg pork belly — smoked and cured by Breki + 1 big onion
                  </td>
                  <td>{gearWeightG(GROUP_GEAR_WEIGHT_G.foodPork)}</td>
                </tr>
                <tr>
                  <td>Food</td>
                  <td>10 apples</td>
                  <td>{gearWeightG(GROUP_GEAR_WEIGHT_G.foodApples)}</td>
                </tr>
                <tr>
                  <td>Food</td>
                  <td>
                    {formatGearNotes(
                      "500 g olive oil",
                      "for dinner mixes (250 g would have been enough for the three of us)"
                    )}
                  </td>
                  <td>{gearWeightG(GROUP_GEAR_WEIGHT_G.foodOil)}</td>
                </tr>
                <tr>
                  <td>Pot</td>
                  <td>
                    750 ml titanium pot with lid — for boiling water and occasionally
                    frying the pork belly with sautéed onions
                  </td>
                  <td>{gearWeightG(GROUP_GEAR_WEIGHT_G.pot)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="packraft-gear-table__title">Food — plan for 5 days</h3>
          <p className="packraft-gear-section__text">
            We were lucky the water was crystal clear and we could filter directly from
            the river. Even so, we treated it with silver-iodine{" "}
            <strong>Microdyn</strong>—a well-established water purifier among US cavers.
            We did not use alum.
          </p>
          <GearSectionWeightTotal
            label="Food"
            grams={sumGearWeightG(FOOD_PLAN_WEIGHT_G)}
          />
          <div className="packraft-table-wrap">
            <table className="packraft-gear-table">
              <thead>
                <tr>
                  <th scope="col">Item</th>
                  <th scope="col">Notes</th>
                  <th scope="col">Weight</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Breakfast</td>
                  <td>5× Trailtopia mix from REI</td>
                  <td>{gearWeightG(FOOD_PLAN_WEIGHT_G.breakfast)}</td>
                </tr>
                <tr>
                  <td>Lunch</td>
                  <td>1 dry salami + 1 smoked gouda + 1 cheddar</td>
                  <td>{gearWeightG(FOOD_PLAN_WEIGHT_G.lunch)}</td>
                </tr>
                <tr>
                  <td>Dinner</td>
                  <td>
                    5× freeze-dried from REI — Peak Refuel, AlpineAire
                  </td>
                  <td>{gearWeightG(FOOD_PLAN_WEIGHT_G.dinner)}</td>
                </tr>
                <tr>
                  <td>Misc</td>
                  <td>
                    2× energy bars, 5×{" "}
                    <a
                      href="https://www.amazon.com/SaltStick-FastChews-Electrolytes-Electrolyte-Hydration/dp/B0BS1MWSRZ"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      SaltStick FastChews electrolytes
                    </a>
                    , 2×{" "}
                    <a
                      href="https://www.amazon.com/SaltStick-Gummies-Electrolytes-Rainbow-Electrolyte/dp/B0F8PDSPKW"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      SaltStick energy gummies, Trailmix leftover from pantry
                    </a>
                  </td>
                  <td>{gearWeightG(FOOD_PLAN_WEIGHT_G.misc)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="packraft-gear-table__title">Managing human waste</h3>
          <p className="packraft-gear-section__text">
            We tried various solutions. YAK tubes were not available to purchase at the
            time; we had DIY PVC tubes and Cleanwaste-style toilets-in-a-tube, but they
            all looked flimsy or too heavy. So we went with the trusted caving groover
            setup—<strong>Curtec</strong> drums—lined with a trash bag before placing the
            used WAG bag inside and tying a knot. Once secure, I put the drum in a tight
            waterproof bag, just for safety, before storing it in the stern of my raft,
            clipped to other gear. The toilet paper that comes with WAG bags was not
            enough, so I carried an extra pack of Dude Wipes that doubled as cleaning at
            night.
          </p>
          <p className="packraft-gear-section__text">
            It is the only smell-proof setup I would trust on a multi-day river trip.
          </p>
          <GearSectionWeightTotal
            label="Human waste"
            grams={sumGearWeightG(HUMAN_WASTE_WEIGHT_G)}
          />
          <div className="packraft-table-wrap">
            <table className="packraft-gear-table">
              <thead>
                <tr>
                  <th scope="col">Item</th>
                  <th scope="col">Notes</th>
                  <th scope="col">Weight</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Plastic drum</td>
                  <td>
                    <span className="packraft-gear-table__lead">
                      <a
                        href="https://www.innermountainoutfitters.com/collections/containers/products/darren-drum?variant=43697382981873"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Curtec 6L
                      </a>
                      <button
                        type="button"
                        className="packraft-gear-photo-btn"
                        aria-label="View photo of Curtec drum at camp"
                        onClick={() => openLightboxForSrc(CURTEC_DRUM_PHOTO_SRC)}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          width="18"
                          height="18"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <polyline points="21 15 16 10 5 21" />
                        </svg>
                      </button>
                    </span>
                    <span className="packraft-gear-table__detail">
                      It can easily double as a seat around camp even when packed with
                      #2
                    </span>
                  </td>
                  <td>{gearWeightG(HUMAN_WASTE_WEIGHT_G.drum)}</td>
                </tr>
                <tr>
                  <td>WAG bags</td>
                  <td>
                    6 total — 4×{" "}
                    <a
                      href="https://www.amazon.com/Cleanwaste-Original-Portable-Toilet-Pack/dp/B001P6ZFG2/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Original WAG Bags
                    </a>
                    {" + 2× "}
                    <a
                      href="https://www.amazon.com/Restop-Solid-Liquid-Waste-Bag/dp/B002L1J4P8"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Restop2
                    </a>
                    <span className="packraft-gear-table__detail">
                      Saved two for the next trip — I would recommend WAG Bags only with
                      this setup — much lighter
                    </span>
                  </td>
                  <td>{gearWeightG(HUMAN_WASTE_WEIGHT_G.wagBags)}</td>
                </tr>
                <tr>
                  <td>Gel</td>
                  <td>
                    {formatGearNotes("Poo Gel ×10", "In case we needed to reuse all WAG bags")}
                  </td>
                  <td>{gearWeightG(HUMAN_WASTE_WEIGHT_G.gel)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <GearGrandTotal />
        </section>

        <section id="travel" className="packraft-section">
          <h2 className="packraft-section__title">One Day at a Time</h2>
          <div className="packraft-table-wrap packraft-table-wrap--schedule">
            <table className="packraft-gear-table packraft-schedule-table">
              <thead>
                <tr>
                  <th scope="col">Day</th>
                  <th scope="col">Notes</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Day 1</td>
                  <td>
                    Travel from the East Coast to Las Vegas; rent a car and drive to
                    the South Rim. Camp on the South Rim.
                  </td>
                </tr>
                <tr>
                  <td>Day 2</td>
                  <td>
                    Drop off the car in the Backcountry Office parking lot (check in
                    with rangers—you can leave the car there for days, free, no special
                    permit needed) and shuttle from South Rim to Lee&apos;s Ferry with
                    Dan—price negotiated over the phone, not listed here. 3&nbsp;PM
                    orientation with river ranger Ben.
                  </td>
                </tr>
                <tr className="packraft-table-row--river">
                  <td>Day 3</td>
                  <td>
                    River day 1
                    <span className="packraft-gear-table__detail">
                      Up before dawn, scouting each rapid — ran Badger, swam Soap Creek,
                      portaged House Rock — camp around mile 18
                    </span>
                  </td>
                </tr>
                <tr id="river-day-2" className="packraft-table-row--river">
                  <td>Day 4</td>
                  <td>
                    <a href="#river-day-2" className="packraft-anchor-link">
                      River day 2
                    </a>
                    <span className="packraft-gear-table__detail">
                      Swim at Indian Dick, then easy miles through the Roaring 20s —
                      side hikes to Red Wall and Nautiloid Canyon. We camped in a quiet
                      spot where a climb of over a thousand vertical feet could put you
                      back in touch—just long enough to send a few words to the people
                      you <LoveToHeart /> the most.
                    </span>
                  </td>
                </tr>
                <tr className="packraft-table-row--river">
                  <td>Day 5</td>
                  <td>
                    River day 3
                    <span className="packraft-gear-table__detail">
                      I stayed dry — Nankoweap granaries — camp on the far bank of the
                      Little Colorado
                    </span>
                  </td>
                </tr>
                <tr className="packraft-table-row--river">
                  <td>Day 6</td>
                  <td>
                    River day 4
                    <span className="packraft-gear-table__detail">
                      Little Colorado River first, then a few swims and Unkar — night
                      at Hance
                    </span>
                  </td>
                </tr>
                <tr className="packraft-table-row--river">
                  <td>Day 7</td>
                  <td>
                    River day 5
                    <span className="packraft-gear-table__detail">
                      Hance at first light (towed the first half—a strainer tree stopped
                      us from cheating it on river left), then a bunch of
                      grade 5–6 — some cheated, some ran — Sockdolager and Grapevine
                      — lemonade at Phantom Ranch, then meticulous gear folding in
                      backpacks on Pipe Creek beach
                    </span>
                  </td>
                </tr>
                <tr>
                  <td>Day 8</td>
                  <td>
                    Early morning exit from Bright Angel Trail — spend the day around
                    South Rim and camp
                  </td>
                </tr>
                <tr>
                  <td>Day 9</td>
                  <td>Travel back to Las Vegas → New Jersey</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="packraft-tide-chart__disclaimer">
            Although we counted around 25 rapids, in reality there are numerous rapids
            and riffles at grade 2–3; most of them are read-and-run.
          </p>
        </section>

        <section id="rapids-flow-chart" className="packraft-section">
          <h2 className="packraft-section__title">Rapids &amp; Flow Chart</h2>
          <p>
            According to{" "}
            <a
              href="https://waterdata.usgs.gov/monitoring-location/USGS-09380000/"
              target="_blank"
              rel="noopener noreferrer"
            >
              USGS data for the Colorado River at Lee&apos;s Ferry
            </a>
            , we expected flows somewhere between about{" "}
            <strong>5,000 cfs and 12,000 cfs</strong> for our launch window—but hitting
            the right rapid at the right flow still felt critical. River character
            changes dramatically with discharge; a line that is forgiving at one cfs
            can feel completely different a few thousand cfs higher or lower.
          </p>
          <p>
            We found various online charts that map daily peak and low water times at
            every mile—similar to the tide table below. River ranger Ben confirmed it
            was more or less accurate for planning
            camps and rapids. Still, unexpected Glen Canyon Dam releases can trigger
            sudden peaks that no spreadsheet predicts.
          </p>

          <h3 className="packraft-gear-table__title">
            Tide chart — Lee&apos;s Ferry to Pipe Creek (miles 0–89)
          </h3>
          <p className="packraft-tide-chart__intro">
            Based on flows of 10,000–16,000 cfs. Excerpt from{" "}
            <em>Tips for Rowing the Grand Canyon</em> tide table: Lees Ferry through
            Pipe Creek. Times are in 24-hour (military) format.
          </p>
          <div className="packraft-table-wrap packraft-tide-chart-wrap">
            <table className="packraft-gear-table packraft-tide-chart">
              <thead>
                <tr>
                  <th scope="col">Mile</th>
                  <th scope="col">Location/Rapid</th>
                  <th scope="col">Peak</th>
                  <th scope="col">Low</th>
                </tr>
              </thead>
              <tbody>
                {TIDE_CHART_LEES_TO_PIPE.map((row) => (
                  <tr key={row.mile}>
                    <td>{row.mile}</td>
                    <td>{row.location}</td>
                    <td>{row.peak}</td>
                    <td>{row.low}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="packraft-tide-chart__disclaimer">
            Times are local; peak and low are relative to Glen Canyon Dam release
            timing.
          </p>
          <aside className="packraft-note packraft-tide-chart__caveat">
            <p>
              This chart is meant to be a helpful tool, particularly to understand
              if the water will be dropping or rising at your camp. The times can vary
              with cfs and the whim of the Bureau of Reclamation. Weekend flow regimes
              may vary as well.
            </p>
          </aside>
        </section>

        <section id="floating-down" className="packraft-section">
          <h2 className="packraft-section__title">FLOAT down the Grand</h2>
          <p>
            From the beginning we wanted to set ourselves up for success—to survive
            packrafting the Grand Canyon without incidents—as well as visit as many
            attractions as possible along the way (you know, those Instagram photos
            everybody takes). So we made a hard rule: we would absolutely not run any
            rapid rated 5, 6, or 7 without prior scouting and a full consultation with
            the team. That rule is critical. If you are a first-timer on the Colorado,
            you should never skip it, no matter how good a kayaker you think you are.
          </p>
          <p>
            We knew we needed to cover about ninety river miles in five days—roughly
            seventeen to eighteen miles a day—with a comfortable, leisurely camp on
            the night between day 5 and 6 before the hike out. By the first afternoon
            it was already clear we could do more: twenty to twenty-one miles without
            being particularly strong rowers, with the river itself carrying us at
            two to three miles per hour. Our biggest challenge was the wind, which
            often held us in place. Giant eddies and side hydraulics were worse: the
            center line would pull the raft downstream while the water along the
            tubes—where we were actually paddling—pushed us upstream. A really weird
            feeling.
          </p>
          <p>
            None of the rapids felt particularly challenging on paper—we followed the
            description of each rapid from the guidebook, which we printed on{" "}
            <strong>TerraSlate</strong> paper, and we carried downloaded satellite maps
            in <strong>Avenza</strong>. Still, it is hard to hit a rapid at the right
            flow in a way that leaves you confident you can run it safely. House Rock
            is the example: we hit it at low flow and it looked scary. The water was
            so low we could not run the river-right line, so we walked it—yes, you can
            absolutely do that with a packraft. Tie the boat to your tow line and walk
            beside it. We used the same technique on many other rapids, in full or in
            part.
          </p>
          <p>
            At Hance and Sockdolager we were able to simply avoid the giant holes and keep the
            trip from becoming a total disaster. That approach most likely let us move
            fast: by the time the evening winds hit, we were already long in camp. It also
            gave us time to visit more attractions and meet other people along the way.
          </p>
          <p>
            We comfortably hit the iconic stops: Redwall Cavern, Nautiloid Canyon, Little
            Colorado, Nankoweap granaries, and the archaeological site in the Unkar Delta.
            We stopped to chat and listen to river advice from river guides,
            fish-and-wildlife staff, and park rangers patrolling the river. The river
            guides on commercial trips were especially helpful—they offered to haul out
            our trash or WAG bags. Being tough hikers, we refused. That was also when one
            of us started calling those giant motorized river boats &ldquo;cattleboats,&rdquo;
            a term that stuck with me all the way to the end of the trip.
          </p>
          <p>
            Along the way we hit our two nemeses: Sockdolager and Grapevine Rapids. We
            talked about them so much that I told Jon he should name his first company
            after Sockdolager—if he ever starts one. We had planned to cheat the giant
            holes and whirlpools; instead Jon somehow made it halfway down one of them by
            climbing along the sheer wall on river left, then pulled off the biggest feat of
            the trip: he swam more than half of it after a foothold broke and he went in the
            water. It was comic-book material. I was paddling upstream to catch an eddy and
            wait for him when I noticed the empty raft floating downriver; ten to fifteen
            seconds later a calm Jon followed, thrown in the air from time to time by one
            of the large waves. By then we were already accustomed to swimming—all of us
            had had our fair share of water.
          </p>
          <p>
            We kept tabs on each other&apos;s flips: Breki 5, Victor 5, Jon 3 (not counting
            his Sockdolager dive). Funny enough, almost all flips happened in lower-grade
            rapids, almost always from a well-timed lateral wave—proof that accidents can
            happen anytime, and that packrafts are not forgiving.
          </p>
          <p>
            Every morning we filtered and filled about four liters for the day ahead. By
            the end of the trip I had learned two was plenty—maybe because I was already
            taking in plenty of Colorado River whenever a rapid held me upside down long
            enough to count as an unplanned hydration break.
          </p>
        </section>

        <div className="packraft-video">
          <div className="packraft-video__embed">
            <iframe
              src="https://www.youtube.com/embed/XY8qEImzrV4"
              title="Grand Canyon packrafting on the Colorado River"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
          <p className="packraft-video__note">
            Tip: in the player, open <strong>Settings</strong> (gear icon) and set quality
            to the highest available—<strong>2160p (4K)</strong>. YouTube often starts at a
            lower resolution.
          </p>
        </div>

        <section id="hiking-out" className="packraft-section">
          <h2 className="packraft-section__title">
            Hike out from Pipe Creek Beach via Bright Angel Trail
          </h2>
          <p>
            This was the part we worried about most. Each of us carried roughly{" "}
            <strong>60&nbsp;lb</strong> packs, so we planned the Bright Angel exit
            carefully and stuck to the plan. We moved fast, but we did not want to
            top out before dawn—mainly because we had no idea what we would do on the
            rim that early, and our campground reservation was for the following night.
            We actually started up the trail earlier than expected—around{" "}
            <strong>6&nbsp;PM</strong> instead of the 9–10&nbsp;PM we had penciled in—which
            turned out to be an advantage: we could take in the canyon from the
            Devil&apos;s Corkscrew in good light.
          </p>
          <p>
            We took a long stop at Indian Garden—two or three hours to eat, drink, and
            rest. By the time we left it was already very late. We hit 3.5 Mile
            Resthouse, cooked again, and rested a long while; same story at 1.5 Mile,
            where sunrise caught up with us and we could admire the orange walls on the
            way up and catch a last glimpse of Pipe Creek beach below. We were out of
            the canyon earlier than we had expected, stopped for a three-hour breakfast
            at the restaurant, checked into the campground, and had an easy night on the
            rim.
          </p>
          <p>
            Along Bright Angel there are plenty of places to stop and recover. The park
            maintains restrooms at four key points—Pipe Creek, Indian Garden, 3.5 Mile
            Resthouse, and 1.5 Mile Resthouse—meant for catching your breath and topping
            up on water. Use them. Before you start, though, check the current trail
            conditions and water availability; pipeline construction and repairs can
            shut off taps or change what is open on any given day.
          </p>
          <p>
            You need to hydrate and keep your calories up on this hike—so suck those
            energy gummies, drink your water, and hit the electrolytes. I figured two
            liters was enough for me: one to sip between rest stops and one labeled
            &ldquo;emergency&rdquo; that mostly means the stretch where you are already too
            tired to argue with yourself. At the end I still walked out one extra liter I
            had filled at Hance—river water I had been nursing since camp, which Jon and I
            finished together at the airport like a grim victory toast to the Colorado.
          </p>
          <p>
            Our suggestion: do not underestimate this hike. Take your time, do not push
            it, and rest, drink, and eat well. If you have to walk out this way, we would
            absolutely recommend doing it the way we did—starting in the evening, long
            breaks in the cool hours, and a slow final push after dawn. We barely saw
            direct sun. With packs this heavy, we would not recommend doing it in hot sun.
          </p>
        </section>

        <section
          id="photos"
          className="packraft-section packraft-gallery"
          aria-label="Photos"
        >
          <div className="packraft-gallery__carousel">
            <button
              type="button"
              className="packraft-gallery__nav packraft-gallery__nav--prev"
              aria-label="Previous gallery page"
              disabled={galleryIndex === 0}
              onClick={() => goToGalleryPage(galleryIndex - 1)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <div
              className="packraft-gallery__viewport"
              onTouchStart={handleGalleryTouchStart}
              onTouchEnd={handleGalleryTouchEnd}
            >
              <div
                className="packraft-gallery__track"
                style={{ transform: `translate3d(-${galleryIndex * 100}%, 0, 0)` }}
              >
                {galleryPages.map((page, pageIndex) => (
                  <div
                    key={`gallery-page-${pageIndex}`}
                    className="packraft-gallery__page"
                    aria-hidden={pageIndex !== galleryIndex}
                  >
                    <ul
                      className="packraft-gallery__grid"
                      style={{ "--gallery-cols": galleryColumns }}
                    >
                      {page.map((image) => (
                        <li key={image.src} className="packraft-gallery__item">
                          <a
                            href={image.src}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(event) => {
                              if (!isDesktopGallery()) return;
                              event.preventDefault();
                              openGalleryLightbox(image.src);
                            }}
                          >
                            <img
                              src={image.src}
                              alt={image.alt}
                              loading="lazy"
                              decoding="async"
                            />
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
            <button
              type="button"
              className="packraft-gallery__nav packraft-gallery__nav--next"
              aria-label="Next gallery page"
              disabled={galleryIndex >= galleryPages.length - 1}
              onClick={() => goToGalleryPage(galleryIndex + 1)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
          {galleryPages.length > 1 && (
            <p className="packraft-gallery__hint" aria-live="polite">
              <span className="packraft-gallery__hint-touch">Swipe </span>
              <span className="packraft-gallery__hint-arrows">or use arrows </span>
              — {galleryIndex + 1} of {galleryPages.length}
            </p>
          )}
        </section>

        <section id="trip-costs" className="packraft-section">
          <h2 className="packraft-section__title">Trip cost &amp; shared expenses</h2>
          <p className="packraft-gear-section__text packraft-costs__disclaimer">
            When we organized this trip, prices were lower—gas was cheaper, and the war
            in Iran had not started yet. The figures below are <strong>shared costs</strong>{" "}
            for the three of us; they do not include personal gear. I already had most of
            my kit ready to go.
          </p>
          <div className="packraft-table-wrap">
            <table className="packraft-gear-table packraft-costs-table">
              <thead>
                <tr>
                  <th scope="col">Expense</th>
                  <th scope="col">Cost</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    Car rental for a week
                    <span className="packraft-gear-table__detail">
                      Rented at the airport through Costco
                    </span>
                  </td>
                  <td>$464</td>
                </tr>
                <tr>
                  <td>Gas</td>
                  <td>$100</td>
                </tr>
                <tr>
                  <td>
                    Shuttle from South Rim to Lee&apos;s Ferry
                    <span className="packraft-gear-table__detail">
                      We used &ldquo;Dan&rdquo;—negotiated the price well before the gas
                      price hike — (928) 606-8245
                    </span>
                  </td>
                  <td>$360</td>
                </tr>
                <tr>
                  <td>
                    Two nights camping on the South Rim
                    <span className="packraft-gear-table__detail">
                      Mather Campground
                    </span>
                  </td>
                  <td>$30</td>
                </tr>
                <tr>
                  <td>River permit</td>
                  <td>$410</td>
                </tr>
                <tr>
                  <td>
                    Food and miscellaneous
                    <span className="packraft-gear-table__detail">
                      Walmart — butane gas, river food
                    </span>
                  </td>
                  <td>$100</td>
                </tr>
              </tbody>
              <tfoot>
                <tr className="packraft-costs-table__total">
                  <th scope="row">Total (three of us)</th>
                  <td>${TRIP_SHARED_EXPENSES_TOTAL_USD.toLocaleString("en-US")}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <p>
            Put those shared costs in context: if you already own most of the gear, a trip
            like this can run as low as about <strong>$1,000</strong> per person once you
            add a flight and a little gear renewal—the drysuit zipper you have been putting
            off, a paddle you actually trust, that sort of thing. Someone just getting into
            packrafting and buying kit from scratch could be closer to <strong>$4,000</strong>.
          </p>
          <p>
            This is a once-in-a-lifetime opportunity for most people. My take: if you are in
            good shape, can afford the time off work, and can live with a hike out at the
            end, you should absolutely experience the river the way we did—private, light,
            and under your own decisions. If you can choose your dates, aim for{" "}
            <strong>mid-May</strong>; that is when we went, and the water and weather
            worked well for us.
          </p>
        </section>

        <div className="packraft-byline packraft-byline--end">
          <p className="packraft-byline__credit">
            Written by: Victor Ursu — 05/30/2026
          </p>
          <ul className="packraft-byline__social">
            <li>
              <a
                href="https://www.facebook.com/Victor.Ursu"
                target="_blank"
                rel="noopener noreferrer"
              >
                Facebook
              </a>
            </li>
            <li>
              <a
                href="https://www.instagram.com/ursuvictor/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Instagram
              </a>
            </li>
          </ul>
        </div>
      </div>

      {lightboxIndex != null && (
        <div
          className="packraft-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Photo preview"
        >
          <button
            type="button"
            className="packraft-lightbox__backdrop"
            aria-label="Close preview"
            onClick={closeGalleryLightbox}
          />
          <button
            type="button"
            className="packraft-lightbox__close"
            aria-label="Close preview"
            onClick={closeGalleryLightbox}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="28"
              height="28"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          <button
            type="button"
            className="packraft-lightbox__nav packraft-lightbox__nav--prev"
            aria-label="Previous photo"
            onClick={() => stepGalleryLightbox(-1)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="32"
              height="32"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <figure className="packraft-lightbox__figure">
            <img
              src={gcGallery.images[lightboxIndex].src}
              alt={gcGallery.images[lightboxIndex].alt}
              className="packraft-lightbox__image"
            />
            <figcaption className="packraft-lightbox__caption">
              {lightboxIndex + 1} of {gcGallery.images.length}
            </figcaption>
          </figure>
          <button
            type="button"
            className="packraft-lightbox__nav packraft-lightbox__nav--next"
            aria-label="Next photo"
            onClick={() => stepGalleryLightbox(1)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="32"
              height="32"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      )}
    </article>
  );
}

export default GrandCanyonPackrafting;
